import { useState, useCallback } from 'react';
import { supabase } from '../lib/supabase';

export interface DamageRecord {
  id: string;
  admin_id: string;
  product_id: string;
  product_name: string;
  quantity: number;
  color_id?: string;
  size_id?: string;
  location_id: string;
  location_name: string;
  description: string;
  image_url?: string;
  reported_by: string;
  reported_by_name: string;
  created_at: string;
}

export interface ReportDamageInput {
  adminId: string;
  userId: string;
  userName: string;
  productId: string;
  productName: string;
  quantity: number;
  colorId?: string;
  sizeId?: string;
  locationId: string;
  locationName: string;
  description: string;
  imageUrl?: string;
}

export function useDamageItems(adminId?: string) {
  const [damageItems, setDamageItems] = useState<DamageRecord[]>([]);
  const [loading, setLoading] = useState(false);

  const fetchDamageItems = useCallback(async () => {
    if (!adminId) return;
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('damaged_items')
        .select('*')
        .eq('admin_id', adminId)
        .order('created_at', { ascending: false })
        .limit(200);

      if (error) throw error;
      setDamageItems(data || []);
    } catch (err) {
      console.error('Error fetching damage items:', err);
    } finally {
      setLoading(false);
    }
  }, [adminId]);

  const reportDamage = useCallback(async (input: ReportDamageInput): Promise<boolean> => {
    try {
      // Atomic: deduct location stock (fail closed) + insert the damage record in
      // one transaction, so a failed insert can never leave stock already removed.
      const { data, error } = await supabase.rpc('record_damage_atomic', {
        p_admin_id: input.adminId,
        p_user_id: input.userId,
        p_user_name: input.userName,
        p_product_id: input.productId,
        p_product_name: input.productName,
        p_quantity: input.quantity,
        p_color_id: input.colorId || null,
        p_size_id: input.sizeId || null,
        p_location_id: input.locationId,
        p_location_name: input.locationName,
        p_description: input.description,
        p_image_url: input.imageUrl || null
      });

      if (error) throw error;
      if (!data?.success) {
        console.error('Damage report rejected:', data?.error, data);
        return false;
      }

      await fetchDamageItems();
      return true;
    } catch (err) {
      console.error('Error reporting damage:', err);
      return false;
    }
  }, [fetchDamageItems]);

  return {
    damageItems,
    loading,
    fetchDamageItems,
    reportDamage
  };
}
